/**
 * Tap-along latency calibration (W1-14, A§8, D§5.2): a click train (100 BPM, 4 count-in clicks) is scheduled on
 * the audio clock; the user taps along (any key except Escape/Tab/Enter/modifier chords and keys typed into a text or
 * value control, or a pointer press outside interactive controls). Taps are matched to the clicks as HEARD (output latency compensated, user calibration
 * excluded): the session's typical error comes from the circular mean of all tap phases, accepted from 150 ms early
 * to 450 ms late (residual Bluetooth latency on Safari is large and positive), and each tap is matched to the click
 * it is closest to around it (latency.ts `resolveTaps`). The median error in ms is the new calibration: positive
 * when the sound reaches the user later than the browser reports. Too few matched taps reject with
 * `calibrationNoTaps`, scattered taps with `calibrationInconsistent`. Escape cancels; so do a new session, an
 * AbortSignal and engine disposal.
 */

import type { Unsubscribe } from '../core/util/types';
import { AudioEngineError } from './errors';
import {
  CALIBRATION_COUNT_IN,
  CALIBRATION_INTERVAL_SEC,
  CALIBRATION_MIN_CONSISTENCY,
  calibrationFromErrors,
  minUsableTaps,
  resolveTaps,
  type TapResolution,
} from './latency';
import type { Timers } from './streaming';

export interface CalibrationContext {
  readonly currentTime: number;
  createBufferSource(): AudioBufferSourceNode;
  createGain(): GainNode;
}

export interface CalibrationDeps {
  readonly ctx: CalibrationContext;
  /** Where the clicks play (master bus: audible even with the metronome volume at 0). */
  readonly bus: AudioNode;
  readonly click: AudioBuffer;
  /** Performance time (ms) at which context time `ctxTime` is heard (latency compensated, no user calibration). */
  readonly heardAt: (ctxTime: number) => number;
  readonly perfNow: () => number;
  readonly timers: Timers;
  /** Subscribes to user taps (performance ms) and to cancel requests. */
  readonly listen: (onTap: (perfMs: number) => void, onCancel: () => void) => Unsubscribe;
}

export interface CalibrationOptions {
  readonly intervalSec?: number;
  readonly countIn?: number;
  /** Seconds between the call and the first click. */
  readonly startDelaySec?: number;
}

export interface CalibrationSession {
  /** Median tap error in whole ms. */
  readonly result: Promise<number>;
  cancel(): void;
}

export function startCalibration(
  deps: CalibrationDeps,
  onTick: (i: number) => void,
  taps: number,
  opts: CalibrationOptions = {},
): CalibrationSession {
  const wanted = Math.max(1, Math.min(64, Math.round(Number.isFinite(taps) ? taps : 16)));
  const interval = opts.intervalSec ?? CALIBRATION_INTERVAL_SEC;
  const countIn = Math.max(0, Math.round(opts.countIn ?? CALIBRATION_COUNT_IN));
  const total = countIn + wanted + Math.max(8, wanted);
  const t0 = deps.ctx.currentTime + (opts.startDelaySec ?? 0.5);
  const clickTime = (i: number): number => t0 + i * interval;

  const sources: AudioBufferSourceNode[] = [];
  const gains: GainNode[] = [];
  for (let i = 0; i < total; i++) {
    const src = deps.ctx.createBufferSource();
    src.buffer = deps.click;
    const g = deps.ctx.createGain();
    g.gain.value = i < countIn ? 0.7 : 1;
    src.connect(g);
    g.connect(deps.bus);
    src.start(clickTime(i));
    sources.push(src);
    gains.push(g);
  }

  const timers: unknown[] = [];
  const tapTimes: number[] = [];
  let finished = false;
  let unlisten: Unsubscribe = () => undefined;
  let resolveResult: (ms: number) => void = () => undefined;
  let rejectResult: (e: unknown) => void = () => undefined;
  const result = new Promise<number>((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });

  const cleanup = (): void => {
    unlisten();
    for (const t of timers) deps.timers.clearTimeout(t);
    timers.length = 0;
    const now = deps.ctx.currentTime;
    for (let i = 0; i < sources.length; i++) {
      if (clickTime(i) > now) {
        try {
          sources[i]?.stop();
        } catch {
          // Already stopped.
        }
        gains[i]?.disconnect();
      }
    }
  };
  const resolution = (): TapResolution => resolveTaps(tapTimes, deps.heardAt(t0), interval * 1000, total, countIn);
  const consistent = (r: TapResolution): boolean => r.consistency >= CALIBRATION_MIN_CONSISTENCY;
  const succeed = (r: TapResolution): void => {
    if (finished) return;
    finished = true;
    cleanup();
    resolveResult(calibrationFromErrors(r.errorsMs));
  };
  const fail = (e: AudioEngineError): void => {
    if (finished) return;
    finished = true;
    cleanup();
    rejectResult(e);
  };

  const onTap = (perfMs: number): void => {
    if (finished || !Number.isFinite(perfMs)) return;
    tapTimes.push(perfMs);
    const r = resolution();
    if (r.errorsMs.length >= wanted && consistent(r)) succeed(r);
  };
  unlisten = deps.listen(onTap, () => {
    fail(new AudioEngineError('calibrationCancelled'));
  });

  const nowMs = deps.perfNow();
  for (let i = 0; i < total; i++) {
    const at = deps.heardAt(clickTime(i));
    timers.push(
      deps.timers.setTimeout(
        () => {
          if (!finished) onTick(i);
        },
        Math.max(0, at - nowMs),
      ),
    );
  }
  const lastHeard = deps.heardAt(clickTime(total - 1));
  timers.push(
    deps.timers.setTimeout(
      () => {
        const r = resolution();
        const usable = minUsableTaps(wanted);
        if (r.errorsMs.length >= usable && consistent(r)) succeed(r);
        else if (r.tapCount >= usable && !consistent(r)) fail(new AudioEngineError('calibrationInconsistent'));
        else fail(new AudioEngineError('calibrationNoTaps'));
      },
      Math.max(0, lastHeard - nowMs + interval * 1000),
    ),
  );

  return {
    result,
    cancel() {
      fail(new AudioEngineError('calibrationCancelled'));
    },
  };
}

const INTERACTIVE =
  'button, a[href], input, select, textarea, [role="button"], [role="menuitem"], [contenteditable="true"]';

/** Controls that take typed or arrow keys themselves (key presses on them are theirs, never taps). */
const KEY_CONTROLS =
  'input, select, textarea, [contenteditable=""], [contenteditable="true"], [role="textbox"], [role="combobox"], ' +
  '[role="listbox"], [role="slider"], [role="spinbutton"]';

/** Input types that behave like buttons (a key on them is a tap; Enter still activates them). */
const BUTTON_INPUT_TYPES: ReadonlySet<string> = new Set(['button', 'submit', 'reset', 'checkbox', 'radio', 'image']);

interface ClosestTarget {
  closest(sel: string): { readonly tagName: string; getAttribute(name: string): string | null } | null;
}

function closestOf(target: EventTarget | null, sel: string): ReturnType<ClosestTarget['closest']> {
  const el = target as Partial<ClosestTarget> | null;
  return el !== null && typeof el.closest === 'function' ? el.closest(sel) : null;
}

/** Whether a key press on `target` belongs to a text or value control (typing, arrows in a slider or a list). */
function isKeyControl(target: EventTarget | null): boolean {
  const hit = closestOf(target, KEY_CONTROLS);
  if (hit === null) return false;
  if (hit.tagName.toUpperCase() !== 'INPUT') return true;
  return !BUTTON_INPUT_TYPES.has((hit.getAttribute('type') ?? 'text').toLowerCase());
}

/**
 * Default tap source, captured on `target` (window):
 *  - keydown: Escape cancels. Tab and Shift+Tab move the focus, Enter activates the focused control (the dialog's
 *    buttons stay keyboard-accessible), Ctrl/Alt/Meta chords and IME composition are ignored, and keys typed into a
 *    text or value control are left to it. Every other key is a tap (auto-repeats are not) and is consumed together
 *    with its keyup, so it neither triggers an editor shortcut, scrolls the page nor presses a focused button
 *    (tapping along with Space never clicks "Cancel").
 *  - pointerdown: primary-button presses outside interactive controls are taps.
 */
export function listenForTaps(target: EventTarget, onTap: (perfMs: number) => void, onCancel: () => void): Unsubscribe {
  /** Keys whose keydown was consumed as a tap: their keyup is consumed too. */
  const held = new Set<string>();
  const keyId = (e: KeyboardEvent): string => (e.code !== '' ? e.code : e.key);
  const onKey = (ev: Event): void => {
    const e = ev as KeyboardEvent;
    if (e.key === 'Escape') {
      onCancel();
      return;
    }
    if (e.isComposing || e.ctrlKey || e.altKey || e.metaKey) return;
    if (e.key === 'Tab' || e.key === 'Enter') return;
    if (isKeyControl(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.repeat) return;
    held.add(keyId(e));
    onTap(e.timeStamp);
  };
  const onKeyUp = (ev: Event): void => {
    const e = ev as KeyboardEvent;
    if (!held.delete(keyId(e))) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const onPointer = (ev: Event): void => {
    const e = ev as PointerEvent;
    if (e.button !== 0) return;
    if (closestOf(e.target, INTERACTIVE) !== null) return;
    onTap(e.timeStamp);
  };
  target.addEventListener('keydown', onKey, { capture: true });
  target.addEventListener('keyup', onKeyUp, { capture: true });
  target.addEventListener('pointerdown', onPointer, { capture: true });
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    target.removeEventListener('keydown', onKey, { capture: true });
    target.removeEventListener('keyup', onKeyUp, { capture: true });
    target.removeEventListener('pointerdown', onPointer, { capture: true });
  };
}
